import { useMemo } from 'react';
import {
  RadarChart as RechartsRadar,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  Legend,
} from 'recharts';
import type { ColumnDef, RadarMapping, VisualizationConfig } from '@deckspace/types';
import { getColorsForIntent, getThemeColors } from '../../theme/index.js';
import { createChartFormatter } from '../../utils/chart-formatter.js';
import { ChartTooltip } from '../../components/ChartTooltip.js';
import { prepRadarSeries } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: RadarMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

export function RadarChart({ rows, columnDefs, mapping, config, width, height }: Props) {
  const series = useMemo(
    () =>
      rows.length === 0 ? { labels: [], datasets: [] } : prepRadarSeries(rows, columnDefs, mapping),
    [rows, columnDefs, mapping]
  );

  const datasets = useMemo(() => series.datasets ?? [], [series.datasets]);

  const chartData = useMemo(
    () =>
      series.labels.map((label, index) => {
        const point: Record<string, string | number> = { subject: label };
        datasets.forEach((dataset) => {
          point[dataset.label] = dataset.values[index] ?? 0;
        });
        return point;
      }),
    [series.labels, datasets]
  );

  if (!series.labels.length || !datasets.length) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No data available
      </div>
    );
  }

  const datasetCount = datasets.length;
  const colors = getColorsForIntent(config.intent, datasetCount);
  const themeColors = getThemeColors();

  const formatValue = createChartFormatter(datasets);

  return (
    <RechartsRadar
      width={width ?? 400}
      height={height ?? 300}
      cx="50%"
      cy="50%"
      outerRadius="80%"
      data={chartData}
    >
      <PolarGrid stroke={themeColors.border} />
      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12, fill: themeColors.textSecondary }} />
      <PolarRadiusAxis tick={{ fontSize: 10, fill: themeColors.textMuted }} />
      <Tooltip content={<ChartTooltip formatter={formatValue} />} />
      <Legend wrapperStyle={{ color: themeColors.textPrimary }} />
      {datasets.map((dataset, index) => (
        <Radar
          key={dataset.label}
          name={dataset.label}
          dataKey={dataset.label}
          stroke={colors[index % colors.length]}
          fill={colors[index % colors.length]}
          fillOpacity={0.3}
        />
      ))}
    </RechartsRadar>
  );
}
